import React, { useState, useRef, useEffect } from "react"
import ReactDOM from "react-dom"
import "./action-menu.css"
import { NotificationPayload, PubSub, TopicsEnum } from "../../../src/utils/pubsub";

interface ActionMenuProps {
  trigger: React.ReactNode,
  menuItems: any[],
  Id?: any,
  entityId?: any
}

const ActionMenu: React.FC<ActionMenuProps> = ({ trigger, menuItems, Id, entityId }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isDropUp, setIsDropUp] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleBodyMouseDown = (data: NotificationPayload) => {
      // Close the menu only if the click is outside the menu
      if (menuRef.current && !menuRef.current.contains((data as any).event?.target) && 
          triggerRef.current && !triggerRef.current.contains((data as any).event?.target)) {
        setIsOpen(false);
      }
    };

    const unsubscribe = PubSub.subscribe(
      TopicsEnum.BodyMouseDown.toString(),
      handleBodyMouseDown
    );

    // Also close on scroll to avoid detached menu
    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      unsubscribe();
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen) { 
      const rect = e.currentTarget.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropUp = spaceBelow < 150;
      
      setIsDropUp(dropUp);
      
      const position: any = {};
      
      if (dropUp) {
        position.bottom = window.innerHeight - rect.top;
        position.top = 'auto';
      } else {
        position.top = rect.bottom;
        position.bottom = 'auto';
      }

      if (rect.left > window.innerWidth / 2) {
         position.right = window.innerWidth - rect.right;
         position.left = 'auto';
         position.transform = 'none';
      } else {
         position.left = rect.left;
         position.right = 'auto';
         position.transform = 'none';
      }
      
      setMenuPosition(position);
    }
    setIsOpen(!isOpen);
  };

  const menuContent = (
    <div 
      className={`dropdown-content ${isDropUp ? 'drop-up' : ''}`} 
      ref={menuRef}
      style={{
        position: 'fixed',
        ...menuPosition,
        marginTop: isDropUp ? 0 : '0.25rem',
        marginBottom: isDropUp ? '0.25rem' : 0,
        zIndex: 999999
      }}
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
    >
      {menuItems.map((item, index) =>
        item.isDivider ? (
          <div key={`divider-${index}`} className="dropdown-separator"></div>
        ) : (
          <button
            key={item.label}
            className={`dropdown-item ${item.isDestructive ? "dropdown-item-destructive" : ""}`}
            onClick={() => {
               if (item.disabled) return;
                if (item.action) {
                  item.action(Id);
                } else if (item.customaction) {
                  item.customaction(entityId);
                }
                setIsOpen(false);
              }}
           disabled={item.disabled}
          >
            {item.icon ? (
              typeof item.icon === "string" ? (
                <img
                  src={item.icon}
                  alt={item.label}
                  width={16}
                  height={16}
                  className="dropdown-item-icon"
                />
              ) : (
                <div className="dropdown-item-icon">
                  {item.icon}
                </div>
              )
            ) : (
              <img
                src="/placeholder.svg"
                alt={item.label}
                width={16}
                height={16}
                className="dropdown-item-icon"
              />
            )}
            <span>{item.label}</span>
          </button>
        )
      )}
    </div>
  );

  return (
    <div id="actionDropdown" className="dropdown-menu-new">
      <div className="dropdown-trigger" onClick={handleToggle} ref={triggerRef}>
        {trigger}
      </div>

      {isOpen && ReactDOM.createPortal(menuContent, document.body)}
    </div>
  )
}

export default ActionMenu
